import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AlertNote from "../../../../../components/AlertNote";
import Toast from "../../../../../components/Toast";
import Modal from "../../../../../components/Modal";
import Button from "../../../../../components/Button";
import { Modules, RouteParamsTypes } from "../../../../../enums";
import ManagerStatementService from "../../../../../services/managerStatement.service";
import CommissionPayoutsSection, { CommissionPayoutsSectionHandle } from "./CommissionPayoutsSection";
import TransactionsSection, { TransactionsSectionHandle } from "./TransactionsSection";
import CleaningSection, { CleaningSectionHandle } from "./CleaningSection";
import OtherDetailsSection from "./OtherDetailsSection";
import DetailsTabButtons from "./DetailsTabButtons";
import StatementKpiSummary from "./StatementKpiSummary";
import { CLEANING_MARGIN_PERCENTAGE } from "../../../../../utils/appConstant";

interface DetailsTabProps {
  statementId: string;
  managerName?: string | null;
  notes: string | null | undefined;
  cleaningDetails?: string | null;
  cleaningSplitAmount?: number | null;
  period?: string;
  status: string;
  statusOptions: any[];
  isLocked: boolean;
  isAdminCaller: boolean;
  onDetailsSaved: (switchTarget: 'none' | 'documents' | 'payments') => void;
  onLinked?: () => void;
}

export interface DetailsTabHandle {
  // Resolves true only when the caller must switch tabs itself (nothing was dirty, so no
  // save/onDetailsSaved ran). Resolves false in every other case - a successful save already
  // switched tabs via onDetailsSaved, and a failed save or a cancelled Completed-confirmation
  // intentionally stays on Details, so the caller has nothing further to do either way.
  trySwitchAwayFromDetails: (target: 'documents' | 'payments') => Promise<boolean>;
}

const DetailsTab = forwardRef<DetailsTabHandle, DetailsTabProps>(({
  statementId, managerName, notes, cleaningDetails, cleaningSplitAmount, period, status, statusOptions, isLocked, isAdminCaller, onDetailsSaved, onLinked,
}, ref) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const base = 'reconciliation.managerStatement.detail.pageTexts.detailsTab.otherDetails';

  const commissionPayoutsRef = useRef<CommissionPayoutsSectionHandle>(null);
  const transactionsRef = useRef<TransactionsSectionHandle>(null);
  const cleaningRef = useRef<CleaningSectionHandle>(null);
  const [commissionTotal, setCommissionTotal] = useState(0);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [cleaningTotal, setCleaningTotal] = useState((cleaningSplitAmount || 0) * (CLEANING_MARGIN_PERCENTAGE / 100));

  const [localNotes, setLocalNotes] = useState(notes || '');
  const [localStatus, setLocalStatus] = useState(status);
  const [saving, setSaving] = useState(false);
  const [savingAndPreview, setSavingAndPreview] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [pendingSwitchTarget, setPendingSwitchTarget] = useState<'none' | 'documents' | 'payments'>('none');
  // Set only when the Completed-confirmation modal was opened by trySwitchAwayFromDetails()
  // (tab-switch autosave path) rather than by the explicit Save button - lets the modal's
  // confirm/cancel handlers resolve that caller's promise without affecting the button's
  // own (non-promise) flow, where this stays null.
  const pendingSwitchResolveRef = useRef<((shouldSwitch: boolean) => void) | null>(null);

  useEffect(() => {
    setLocalNotes(notes || '');
    setLocalStatus(status);
  }, [notes, status]);

  const handleBeforeSave = useCallback(() => {
    // Once locked (completed/paid), the invoice and transaction selections are frozen and
    // the backend rejects further writes to them (409) - skip re-saving them here so a
    // locked statement can still save Notes or transition status (e.g. completed -> paid).
    if (isLocked) {
      return Promise.resolve(undefined);
    }

    return Promise.all([
      commissionPayoutsRef.current?.save(),
      transactionsRef.current?.save(),
      cleaningRef.current?.save(),
    ]).then(() => undefined);
  }, [isLocked]);

  const handleNotesChange = useCallback((e: any) => {
    setLocalNotes(e.target.value);
  }, []);

  const handleStatusChange = useCallback((e: any) => {
    setLocalStatus(e.target.value);
  }, []);

  const performSave = useCallback((switchTarget: 'none' | 'documents' | 'payments'): Promise<boolean> => {
    switchTarget !== 'none' ? setSavingAndPreview(true) : setSaving(true);

    return handleBeforeSave()
      .then(() => ManagerStatementService.updateDetails(statementId, { notes: localNotes || null, status: localStatus }))
      .then(() => {
        toast.success(<Toast message={t(`${base}.saveSuccess`)} />, { icon: false });
        setSaving(false);
        setSavingAndPreview(false);
        onDetailsSaved(switchTarget);
        return true;
      })
      .catch(() => {
        setSaving(false);
        setSavingAndPreview(false);
        return false;
      });
  }, [statementId, localNotes, localStatus, handleBeforeSave, onDetailsSaved, t]);

  // Marking a statement Completed is a one-way trigger for the invoice/transaction lock
  // (see isLocked above) - confirm with the user before it saves, same as the Invoice
  // module's "Mark Completed" confirmation. Only asked on the actual draft->completed
  // transition, not on every subsequent save of an already-completed statement.
  const handleSave = useCallback((switchToDocuments: boolean) => {
    const target: 'none' | 'documents' = switchToDocuments ? 'documents' : 'none';

    if (localStatus === 'completed' && status !== 'completed') {
      setPendingSwitchTarget(target);
      setShowCompleteConfirm(true);
      return;
    }

    performSave(target);
  }, [localStatus, status, performSave]);

  const handleCompleteConfirmClose = useCallback(() => {
    setShowCompleteConfirm(false);
    const resolveSwitch = pendingSwitchResolveRef.current;
    pendingSwitchResolveRef.current = null;
    // Cancelled - nothing is saved, stay on Details. Caller (if any) must not switch tabs.
    resolveSwitch?.(false);
  }, []);

  const handleCompleteConfirm = useCallback(() => {
    setShowCompleteConfirm(false);
    const resolveSwitch = pendingSwitchResolveRef.current;
    pendingSwitchResolveRef.current = null;
    performSave(pendingSwitchTarget).then(() => {
      // performSave already switched tabs itself via onDetailsSaved on success (or stayed on
      // Details on failure) - either way the caller has nothing further to do.
      resolveSwitch?.(false);
    });
  }, [performSave, pendingSwitchTarget]);

  const handleCancel = useCallback(() => {
    navigate(Modules.reconciliation + RouteParamsTypes.managerStatement);
  }, [navigate]);

  const isAnyDirty = useCallback(() => {
    if (isLocked) return false;

    return Boolean(
      commissionPayoutsRef.current?.isDirty()
      || transactionsRef.current?.isDirty()
      || cleaningRef.current?.isDirty()
    ) || localNotes !== (notes || '') || localStatus !== status;
  }, [isLocked, localNotes, notes, localStatus, status]);

  const trySwitchAwayFromDetails = useCallback((target: 'documents' | 'payments'): Promise<boolean> => {
    if (!isAnyDirty()) {
      // Nothing to save - the caller may switch tabs immediately.
      return Promise.resolve(true);
    }

    if (localStatus === 'completed' && status !== 'completed') {
      return new Promise<boolean>((resolve) => {
        pendingSwitchResolveRef.current = resolve;
        setPendingSwitchTarget(target);
        setShowCompleteConfirm(true);
      });
    }

    return performSave(target).then(() => false);
  }, [isAnyDirty, localStatus, status, performSave]);

  useImperativeHandle(ref, () => ({ trySwitchAwayFromDetails }), [trySwitchAwayFromDetails]);

  return (
    <>
      <div className="card-body">
        {isLocked && (
          <div className="mb-6">
            <AlertNote variant="danger">
              {t('reconciliation.managerStatement.detail.pageTexts.detailsTab.lockedBanner')}
            </AlertNote>
          </div>
        )}

        <StatementKpiSummary commissionTotal={commissionTotal} cleaningTotal={cleaningTotal} transactionsTotal={transactionsTotal} />

        <div className="mb-6">
          <CommissionPayoutsSection
            ref={commissionPayoutsRef}
            statementId={statementId}
            managerName={managerName}
            readOnly={isLocked}
            onTotalsChange={setCommissionTotal}
            onLinked={onLinked}
          />
        </div>

        <div className="mb-6">
          <CleaningSection
            ref={cleaningRef}
            statementId={statementId}
            cleaningDetails={cleaningDetails}
            cleaningSplitAmount={cleaningSplitAmount}
            period={period}
            readOnly={isLocked}
            onTotalsChange={setCleaningTotal}
          />
        </div>

        <div className="mb-6">
          <TransactionsSection
            ref={transactionsRef}
            statementId={statementId}
            managerName={managerName}
            readOnly={isLocked}
            onTotalsChange={setTransactionsTotal}
            onLinked={onLinked}
          />
        </div>

        <hr className="my-6 mx-n4" />

        <div>
          <OtherDetailsSection
            notes={localNotes}
            status={localStatus}
            originalStatus={status}
            statusOptions={statusOptions}
            isAdminCaller={isAdminCaller}
            onNotesChange={handleNotesChange}
            onStatusChange={handleStatusChange}
          />
        </div>
      </div>

      <hr className="m-0" />

      <DetailsTabButtons
        saving={saving}
        savingAndPreview={savingAndPreview}
        saveDisabled={status === 'paid'}
        onSave={() => handleSave(false)}
        onSaveAndPreview={() => handleSave(true)}
        onCancel={handleCancel}
      />

      <Modal
        open={showCompleteConfirm}
        onClose={handleCompleteConfirmClose}
        maxWidth="sm"
        title={t(`${base}.completeConfirmModal.title`)}
        content={t(`${base}.completeConfirmModal.message`)}
        action={(
          <>
            <Button
              className="btn btn-outline-secondary me-3"
              text={t(`${base}.completeConfirmModal.buttons.cancel`)}
              onClick={handleCompleteConfirmClose}
            />
            <Button
              className="btn btn-success"
              text={t(`${base}.completeConfirmModal.buttons.confirm`)}
              onClick={handleCompleteConfirm}
            />
          </>
        )}
      />
    </>
  );
});

DetailsTab.displayName = 'DetailsTab';

export default DetailsTab;
